<template>
  <div>
    <b-form-row>
      <b-col>
        <b-form-group
          v-if="externalSurveyInstanceId"
          :label="$t('Webportal.SurveyInstanceId')"
          label-size="sm"
        >
          <span class="font-weight-bold">{{ externalSurveyInstanceId }}</span>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          v-if="surveyInstance"
          :label="$t('Webportal.Survey')"
          label-size="sm"
        >
          {{ surveyInstance.Survey.Title }}
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          v-if="statusId != null"
          :label="$t('Webportal.Status')"
          label-size="sm"
        >
          <b-dropdown
            v-if="isStatusSelectionEnabled"
            size="sm"
            variant="primary"
            link-class="font-weight-bold"
          >
            <template #button-content>
              <span class="font-weight-bold">
                <font-awesome-icon
                  :icon="['far', getImprovementStatusIcon(statusId)]"
                />
                {{ getImprovementStatusText(statusId) }}
              </span>
            </template>

            <b-dropdown-item  v-for="nextStatus in surveyImprovement.NextAllowedSurveyImprovementStatuses"
                              :key="nextStatus"
                              @click="nextStatusClicked(nextStatus)">
              <font-awesome-icon :icon="['far', getImprovementStatusIcon(nextStatus)]" />
              {{ getImprovementStatusText(nextStatus) }}
            </b-dropdown-item>
          </b-dropdown>

          <span v-else class="font-weight-bold">
            <template v-if="surveyImprovement">
              <font-awesome-icon
                :icon="['far', getImprovementStatusIcon(statusId)]"
              />
              {{ getImprovementStatusText(statusId) }}
            </template>
            <template v-else>
              {{ getSurveyInstanceStatusText(statusId) }}
            </template>
          </span>

          <template v-if="surveyImprovement && statusHistoryVisible">
            <b-button
              id="popover-status-history"
              class="ml-2"
              size="sm"
              variant="outline-primary"
            >
              <font-awesome-icon :icon="['fas', 'history']" />
            </b-button>
            <b-popover
              target="popover-status-history"
              triggers="hover focus"
              placement="bottom"
              custom-class="mw-100"
              :title="$t('Webportal.StatusHistory')"
            >
              <b-list-group
                v-if="
                  surveyImprovement.History && surveyImprovement.History.length > 0
                "
                flush
              >
                <b-list-group-item
                  v-for="(history, index) in surveyImprovement.History"
                  :key="index"
                >
                  <b-row>
                    <b-col>
                      <span class="font-weight-bold">
                        <font-awesome-icon
                          :icon="[
                            'far',
                            getImprovementStatusIcon(history.StatusId),
                          ]"
                        />
                        {{ getImprovementStatusText(history.StatusId) }}
                      </span>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col cols="auto">
                      {{ history.ModifiedAt | msm_datetime }}
                    </b-col>
                    <b-col>
                      {{ getModifierInfo(history) }}
                    </b-col>
                  </b-row>
                </b-list-group-item>
              </b-list-group>
              <span v-else>
                {{ $t("Webportal.NoDataFoundPlain") }}
              </span>
            </b-popover>
          </template>
        </b-form-group>
      </b-col>
    </b-form-row>

    <b-form-row v-if="surveyInstance">
      <b-col>
        <b-form-row>
          <b-col>
            <b-form-group :label="$t('Webportal.Customer')" label-size="sm">
              {{ surveyInstance.Client.Name }}
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group :label="$t('Webportal.LocationId')" label-size="sm">
              {{ surveyInstance.ClientLocation.ClientLocationIdOnClientSite }}
            </b-form-group>
          </b-col>
        </b-form-row>
        <p v-if="!surveyImprovement && notificationHint">
          <span class="text-danger">
            <font-awesome-icon :icon="['fas', 'info-circle']" /> {{ notificationHint }}:
          </span>
          <b-link :to="notificationSettingsRoute">{{ $t("Webportal.NotificationSettings") }}</b-link>
        </p>
      </b-col>
      <b-col cols="4">
        <b-form-group :label="$t('Webportal.LocationAddress')" label-size="sm">
          {{ surveyInstance.ClientLocation.Name }}<br />
          {{ surveyInstance.ClientLocation.Address }}<br />
          <span v-if="surveyInstance.ClientLocation.Address2">
            {{ surveyInstance.ClientLocation.Address2 }}
            <br />
          </span>
          {{ surveyInstance.ClientLocation.City }},
          {{ surveyInstance.ClientLocation.CountryId }} -
          {{ surveyInstance.ClientLocation.PostalCode }}
        </b-form-group>
      </b-col>
    </b-form-row>
  </div>
</template>

<script>
import SurveyImprovementMixin from "@/mixins/SurveyImprovementMixin";

export default {
  name: "SurveyImprovementHeader",

  mixins: [SurveyImprovementMixin],

  props: {
    surveyImprovement: null,
    surveyInstance: null,
    statusSelection: { default: false },
    statusHistoryVisible: { default: true },
    relatedSurveyImprovementsVisible: { default: true },
  },

  computed: {
    externalSurveyInstanceId() {
      return (
        this.surveyImprovement?.ExternalSurveyInstanceId ??
        this.surveyInstance?.ExternalSurveyInstanceId ??
        this.surveyInstance?.SurveyInstance?.ExternalSurveyInstanceId
      );
    },

    statusId() {
      return (
        this.surveyImprovement?.StatusId ??
        this.surveyInstance?.SurveyInstance?.SurveyInstanceStatusId
      );
    },

    isStatusSelectionEnabled() {
      return (
        this.statusSelection && !_.isEmpty(this.surveyImprovement?.NextAllowedSurveyImprovementStatuses)
      );
    },

    notificationHint() {
      return this.$t('Webportal.Actions.SurveyImprovement.NotificationNotice');
    }
  },

  methods: {
    onStatusChanged(value) {
      this.$emit("statusChanged", value);
    },

    nextStatusClicked(nextStatusId) {
      // Show confirmation dialog before setting final status
      if (
        nextStatusId == this.SurveyImprovementStatus.Approved ||
        nextStatusId == this.SurveyImprovementStatus.Revised ||
        nextStatusId == this.SurveyImprovementStatus.Done ||
        // nextStatusId == this.SurveyImprovementStatus.Dismissed ||
        nextStatusId == this.SurveyImprovementStatus.RevisedApproved ||
        nextStatusId == this.SurveyImprovementStatus.RevisedDismissed
      ) {
        const h = this.$createElement;

        this.$bvModal
          .msgBoxConfirm(
            [
              h("div", {
                domProps: {
                  innerHTML: this.$t(
                    "Webportal.Dialog.ConfirmStatusChange.Text",
                    {
                      status: `<b>${this.getImprovementStatusText(
                        nextStatusId
                      )}</b>`,
                    }
                  ),
                },
              }),
            ],
            {
              title: this.$t("Webportal.Dialog.ConfirmStatusChange.Title"),
              okTitle: this.$t("Webportal.Dialog.Ok"),
              cancelTitle: this.$t("Webportal.Dialog.Cancel"),
              hideHeaderClose: false,
            }
          )
          .then((value) => {
            if (value) {
              this.onStatusChanged(nextStatusId);
            }
          });
      } else this.onStatusChanged(nextStatusId);
    },

    getModifierInfo(history) {
      if (!history.ModifiedBy) return '';

      if (!history.ModifiedBy.Email)
        return `${history.ModifiedBy.FirstName} ${history.ModifiedBy.LastName}`;
      return `${history.ModifiedBy.FirstName} ${history.ModifiedBy.LastName} (${history.ModifiedBy.Email})`;
    }
  },
};
</script>
